import React, { useState, useEffect, useRef } from "react";
import { Input, Row, Col } from "reactstrap";
import {
  parsePhoneNumberFromString,
  getExampleNumber,
} from "libphonenumber-js";
import { checkExistingCustomer } from "store/actions/customerAction";
import { useDispatch } from "react-redux";

const CustomerData = ({
  customerData,
  setCustomerData,
  fieldErrors,
  setFieldErros,
  setIsValid,
  previousPhoneNumber = "",
  bgColor,
}) => {
  const dispatch = useDispatch();
  const [previousValues, setPreviousValues] = useState({
    name: customerData?.name || "",
    phoneNo: "",
    gender: customerData?.gender || "",
  });
  const [customerExistSuggestion, setCustomerExistSuggestion] = useState("");
  const debounceRef = useRef(null);
  const [isCheckingCustomer, setIsCheckingCustomer] = useState(false);

  const examplePhoneNumber =
    getExampleNumber("US")?.formatInternational() || "+1 202 555 0143";

  const handleChange = (field, value) => {
    let newValue = value;
    if (field === "phoneNo") {
      if (newValue.length === 1 && newValue !== "+") {
        newValue = "";
      } else {
        newValue = newValue.replace(/[^\d+]/g, "");
        if (newValue.indexOf("+") > 0) {
          newValue = newValue.replace(/\+/g, "");
        }
      }

      try {
        const phoneNumber = parsePhoneNumberFromString(newValue);
        if (!phoneNumber?.isValid()) {
          updateFieldErrors("phone", `Invalid phone number ${newValue}`);
        } else {
          updateFieldErrors("phone", "");
          newValue = phoneNumber.formatInternational();
        }
      } catch {
        updateFieldErrors("phone", "Invalid phone number");
      }
    } else if (field === "name") {
      if (newValue?.length < 3) {
        updateFieldErrors("name", "Name must contain atleast 3 characters");
      } else {
        updateFieldErrors("name", "");
      }
    } else {
      if (newValue === "") {
        updateFieldErrors("gender", "Required");
      } else {
        updateFieldErrors("gender", "");
      }
    }
    setCustomerData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const updateFieldErrors = (field, error) => {
    setFieldErros((prev) => ({
      ...prev,
      [field]: error,
    }));
  };
  const clearFieldsError = () => {
    setFieldErros({
      name: "",
      phone: "",
      gender: "",
    });
  };

  useEffect(() => {
    const hasErrors =
      fieldErrors.name ||
      fieldErrors.phone ||
      fieldErrors.gender ||
      isCheckingCustomer;
    const allFieldsFilled =
      customerData.name?.trim() &&
      customerData.phoneNo?.trim() &&
      customerData.gender?.trim();

    setIsValid(Boolean(allFieldsFilled && !hasErrors));
  }, [customerData, fieldErrors, setIsValid, isCheckingCustomer]);

  useEffect(() => {
    if (fieldErrors.phone || !customerData.phoneNo) {
      if (customerExistSuggestion) {
        setCustomerExistSuggestion("");
        setCustomerData((prev) => ({
          ...prev,
          name: previousValues?.name,
          gender: previousValues?.gender,
        }));
      }
      setIsCheckingCustomer(false);
      return;
    }

    if (previousPhoneNumber && previousPhoneNumber === customerData.phoneNo) {
      setIsCheckingCustomer(false);
      return;
    }

    setIsCheckingCustomer(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await dispatch(
          checkExistingCustomer(customerData.phoneNo)
        );
        if (response?.success) {
          if (!previousPhoneNumber) {
            setCustomerExistSuggestion("Account already exists");
            setPreviousValues((prev) => ({
              ...prev,
              name: customerData?.name,
              phoneNo: customerData?.phoneNo,
              gender: customerData?.gender,
            }));
            setCustomerData((prev) => ({
              ...prev,
              name: response?.customerName,
              gender: response?.customerGender,
              addToExisting: true,
            }));
            clearFieldsError();
          } else {
            updateFieldErrors("phone", "Phone no. is already in use");
          }
        } else {
          if (customerExistSuggestion) {
            setCustomerExistSuggestion("");
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsCheckingCustomer(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [customerData.phoneNo, fieldErrors.phone, dispatch]);

  useEffect(() => {
    if (!customerExistSuggestion) {
      setCustomerData((prev) => ({
        ...prev,
        name: previousValues.name,
        gender: previousValues.gender,
        addToExisting: false,
      }));
    }
  }, [customerExistSuggestion]);

  return (
    <Row className="d-flex border-top border-lighter">
      <Col xl={4} lg={4} md={12} sm={12} xs={12} className="px-0 mt-2">
        <div className="d-flex flex-column align-items-start pr-0 pr-lg-2">
          <span className="text-dark fs--15 fw--500">Customer Name</span>
          <Input
            type="text"
            name="name"
            id="name"
            className={`text-dark fs--15 ${
              fieldErrors?.name
                ? "custom-error-border-focus"
                : "custom-border-focus"
            }`}
            placeholder="Customer Name"
            value={customerData.name}
            disabled={customerData?.addToExisting && customerExistSuggestion}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          {fieldErrors?.name && (
            <Col className="col-12 justify-content-start fs--12 text-danger my-1 px-0">
              <div>{fieldErrors?.name}</div>
            </Col>
          )}
        </div>
      </Col>

      <Col xl={4} lg={4} md={12} sm={12} xs={12} className="mt-2 px-0">
        <div className="d-flex flex-column align-items-start px-0 px-lg-1">
          <span className="text-dark fs--15 fw--500">Phone Number</span>
          <Input
            type="tel"
            name="phone"
            id="phone"
            className={`text-dark fs--15 ${
              fieldErrors?.phone
                ? "custom-error-border-focus"
                : "custom-border-focus"
            }`}
            placeholder={examplePhoneNumber || "Phone Number"}
            value={customerData.phoneNo}
            onChange={(e) => handleChange("phoneNo", e.target.value)}
          />
          {fieldErrors?.phone && (
            <Col className="col-12 justify-content-start fs--12 text-danger my-1 px-0">
              <div>{fieldErrors?.phone}</div>
            </Col>
          )}
          {customerExistSuggestion && (
            <Col className="col-12 justify-content-start fs--12 text-success my-1 px-0">
              <div>{customerExistSuggestion}</div>
            </Col>
          )}
        </div>
      </Col>

      <Col xl={4} lg={4} md={12} sm={12} xs={12} className="mt-2 px-0">
        <div className="d-flex flex-column align-items-start pl-0 pl-lg-2">
          <span className="text-dark fs--15 fw--500">Gender</span>
          <Input
            type="select"
            name="gender"
            id="gender"
            className={`text-dark fs--15 ${
              fieldErrors?.gender
                ? "custom-error-border-focus"
                : "custom-border-focus"
            }`}
            value={customerData.gender}
            onChange={(e) => handleChange("gender", e.target.value)}
            disabled={customerData?.addToExisting && customerExistSuggestion}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Input>
          {fieldErrors?.gender && (
            <Col className="col-12 justify-content-start fs--12 text-danger my-1 px-0">
              <div>{fieldErrors?.gender}</div>
            </Col>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default CustomerData;
